import { supabase } from '../lib/supabase';

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => toCamelCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      acc[camelKey] = toCamelCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => toSnakeCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
      acc[snakeKey] = toSnakeCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

export const deliverableService = {
  // Get all deliverables for a project with dependencies and resources
  async getByProject(projectId) {
    try {
      const { data, error } = await supabase
        ?.from('deliverables')
        ?.select(`
          *,
          dependencies:deliverable_dependencies!deliverable_dependencies_deliverable_id_fkey(
            depends_on_id
          ),
          resources:deliverable_resource_assignments(
            start_date,
            end_date,
            resource:resources!deliverable_resource_assignments_resource_id_fkey(*)
          )
        `)
        ?.eq('project_id', projectId)
        ?.order('custom_order', { ascending: true, nullsFirst: false })
        ?.order('sort_order', { ascending: true })
        ?.order('start_date', { ascending: true });

      if (error) throw error;

      // Transform data to include flat arrays
      const transformed = (data || [])?.map(deliverable => ({
        ...toCamelCase(deliverable),
        dependsOn: deliverable?.dependencies?.map(d => d?.depends_on_id) || [],
        assignedResources: deliverable?.resources?.map(r => ({
          ...toCamelCase(r?.resource),
          allocationStartDate: r?.start_date || null,
          allocationEndDate: r?.end_date || null
        })) || []
      }));

      return { data: transformed, error: null };
    } catch (error) {
      console.error('Error fetching deliverables:', error?.message);
      return { data: null, error };
    }
  },

  // Get single deliverable by ID
  async getById(id) {
    try {
      const { data, error } = await supabase
        ?.from('deliverables')
        ?.select(`
          *,
          dependencies:deliverable_dependencies!deliverable_dependencies_deliverable_id_fkey(
            depends_on_id
          ),
          resources:deliverable_resource_assignments(
            start_date,
            end_date,
            resource:resources!deliverable_resource_assignments_resource_id_fkey(*)
          )
        `)
        ?.eq('id', id)
        ?.single();

      if (error) throw error;

      const transformed = {
        ...toCamelCase(data),
        dependsOn: data?.dependencies?.map(d => d?.depends_on_id) || [],
        assignedResources: data?.resources?.map(r => ({
          ...toCamelCase(r?.resource),
          allocationStartDate: r?.start_date || null,
          allocationEndDate: r?.end_date || null
        })) || []
      };

      return { data: transformed, error: null };
    } catch (error) {
      console.error('Error fetching deliverable:', error?.message);
      return { data: null, error };
    }
  },

  // Create new deliverable
  async create(deliverableData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { dependsOn, assignedResources, resourceDates, ...coreData } = deliverableData;
      const snakeData = toSnakeCase({ ...coreData, createdBy: user?.id });

      // Insert deliverable
      const { data, error } = await supabase
        ?.from('deliverables')
        ?.insert(snakeData)
        ?.select()
        ?.single();

      if (error) throw error;

      // Insert dependencies if provided
      if (dependsOn && dependsOn?.length > 0) {
        const dependencies = dependsOn?.map(dependsOnId => ({
          deliverable_id: data?.id,
          depends_on_id: dependsOnId
        }));

        const { error: depError } = await supabase
          ?.from('deliverable_dependencies')
          ?.insert(dependencies);

        if (depError) throw depError;
      }

      // Insert resource assignments if provided
      if (assignedResources && assignedResources?.length > 0) {
        const assignments = assignedResources?.map(resourceId => ({
          deliverable_id: data?.id,
          resource_id: resourceId,
          start_date: resourceDates?.[resourceId]?.startDate || null,
          end_date: resourceDates?.[resourceId]?.endDate || null
        }));

        const { error: resError } = await supabase
          ?.from('deliverable_resource_assignments')
          ?.insert(assignments);

        if (resError) throw resError;
      }

      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error creating deliverable:', error?.message);
      return { data: null, error };
    }
  },

  // Update deliverable
  async update(id, deliverableData) {
    try {
      const { dependsOn, assignedResources, resourceDates, ...coreData } = deliverableData;
      const snakeData = toSnakeCase(coreData);

      // Update deliverable
      const { data, error } = await supabase
        ?.from('deliverables')
        ?.update(snakeData)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;

      // Update dependencies if provided
      if (dependsOn !== undefined) {
        // Delete existing dependencies
        await supabase
          ?.from('deliverable_dependencies')
          ?.delete()
          ?.eq('deliverable_id', id);

        // Insert new dependencies
        if (dependsOn?.length > 0) {
          const dependencies = dependsOn?.map(dependsOnId => ({
            deliverable_id: id,
            depends_on_id: dependsOnId
          }));

          const { error: depError } = await supabase
            ?.from('deliverable_dependencies')
            ?.insert(dependencies);

          if (depError) throw depError;
        }
      }

      // Update resource assignments if provided
      if (assignedResources !== undefined) {
        // Delete existing assignments
        await supabase
          ?.from('deliverable_resource_assignments')
          ?.delete()
          ?.eq('deliverable_id', id);

        // Insert new assignments
        if (assignedResources?.length > 0) {
          const assignments = assignedResources?.map(resourceId => ({
            deliverable_id: id,
            resource_id: resourceId,
            start_date: resourceDates?.[resourceId]?.startDate || null,
            end_date: resourceDates?.[resourceId]?.endDate || null
          }));

          const { error: resError } = await supabase
            ?.from('deliverable_resource_assignments')
            ?.insert(assignments);

          if (resError) throw resError;
        }
      }

      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating deliverable:', error?.message);
      return { data: null, error };
    }
  },

  // Delete deliverable
  async delete(id) {
    try {
      const { error } = await supabase
        ?.from('deliverables')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting deliverable:', error?.message);
      return { error };
    }
  },

  // Bulk shift all dates for a project by a given number of days (positive = later, negative = earlier)
  // Or shift to align the earliest start date to a specific date
  async bulkShiftDates(projectId, { days, specificDate }) {
    try {
      // Fetch all deliverables for the project that have dates
      const { data, error } = await supabase
        ?.from('deliverables')
        ?.select('id, start_date, end_date')
        ?.eq('project_id', projectId);

      if (error) throw error;

      const items = (data || [])?.filter(d => d?.start_date || d?.end_date);
      if (!items?.length) return { error: null, count: 0 };

      let shiftDays = days;

      // If specificDate mode: compute offset from earliest start_date
      if (specificDate !== undefined && specificDate !== null) {
        const startDates = items
          ?.filter(d => d?.start_date)
          ?.map(d => new Date(d?.start_date));
        if (!startDates?.length) return { error: null, count: 0 };
        const earliest = new Date(Math.min(...startDates?.map(d => d?.getTime())));
        const target = new Date(specificDate);
        // Compute difference in days
        const msPerDay = 1000 * 60 * 60 * 24;
        shiftDays = Math.round((target?.getTime() - earliest?.getTime()) / msPerDay);
      }

      if (shiftDays === 0) return { error: null, count: 0 };

      // Build updates
      const addDaysToDate = (dateStr, d) => {
        if (!dateStr) return null;
        const dt = new Date(dateStr);
        dt?.setDate(dt?.getDate() + d);
        return dt?.toISOString()?.split('T')?.[0];
      };

      const promises = items?.map(item => {
        const updates = {};
        if (item?.start_date) updates.start_date = addDaysToDate(item?.start_date, shiftDays);
        if (item?.end_date) updates.end_date = addDaysToDate(item?.end_date, shiftDays);
        return supabase
          ?.from('deliverables')
          ?.update(updates)
          ?.eq('id', item?.id);
      });

      await Promise.all(promises);
      return { error: null, count: items?.length, shiftDays };
    } catch (error) {
      console.error('Error bulk shifting dates:', error?.message);
      return { error };
    }
  },

  // Bulk update custom_order for reordering
  async updateOrders(updates) {
    // updates: [{ id, customOrder }]
    try {
      const promises = updates?.map(({ id, customOrder }) =>
        supabase
          ?.from('deliverables')
          ?.update({ custom_order: customOrder })
          ?.eq('id', id)
      );
      await Promise.all(promises);
      return { error: null };
    } catch (error) {
      console.error('Error updating orders:', error?.message);
      return { error };
    }
  },

  // Compute and persist order numbers for items that lack custom_order, using start_date as default sort
  async initOrdersFromStartDate(items) {
    // items: array of camelCase deliverable objects (all types: deliverable, task, subtask)
    // Only process items that have no customOrder set
    const needsOrder = items?.filter(i => i?.customOrder == null || i?.customOrder === '');
    if (!needsOrder?.length) return { error: null };

    // --- Deliverables ---
    const deliverables = items?.filter(i => i?.rowType === 'deliverable' || (!i?.rowType && !i?.parentId));
    const sortedDeliverables = [...deliverables]?.sort((a, b) => {
      const ad = a?.startDate ? new Date(a?.startDate) : new Date(9999, 0);
      const bd = b?.startDate ? new Date(b?.startDate) : new Date(9999, 0);
      return ad - bd;
    });
    // Assign whole numbers, preserving existing order for those that already have one
    const deliverableOrderMap = {};
    // First pass: collect existing orders
    deliverables?.forEach(d => {
      if (d?.customOrder != null && d?.customOrder !== '') {
        deliverableOrderMap[d?.id] = Number(d?.customOrder);
      }
    });
    // Second pass: assign to those without
    let deliverableCounter = 1;
    sortedDeliverables?.forEach(d => {
      if (deliverableOrderMap?.[d?.id] == null) {
        // Skip numbers already taken
        while (Object.values(deliverableOrderMap)?.includes(deliverableCounter)) deliverableCounter++;
        deliverableOrderMap[d?.id] = deliverableCounter++;
      }
    });

    // --- Tasks ---
    const tasks = items?.filter(i => i?.rowType === 'task');
    const taskOrderMap = {};
    // Group tasks by parent deliverable
    const tasksByParent = {};
    tasks?.forEach(t => {
      const pid = t?.parentId;
      if (!tasksByParent?.[pid]) tasksByParent[pid] = [];
      tasksByParent?.[pid]?.push(t);
    });
    Object.entries(tasksByParent)?.forEach(([parentId, parentTasks]) => {
      const parentOrder = deliverableOrderMap?.[parentId] ?? 1;
      const sorted = [...parentTasks]?.sort((a, b) => {
        const ad = a?.startDate ? new Date(a?.startDate) : new Date(9999, 0);
        const bd = b?.startDate ? new Date(b?.startDate) : new Date(9999, 0);
        return ad - bd;
      });
      // Collect existing task orders for this parent
      const existingTaskOrders = {};
      parentTasks?.forEach(t => {
        if (t?.customOrder != null && t?.customOrder !== '') {
          const decimal = Math.round((Number(t?.customOrder) - Math.floor(Number(t?.customOrder))) * 10);
          existingTaskOrders[t?.id] = decimal;
        }
      });
      let taskCounter = 1;
      sorted?.forEach(t => {
        if (t?.customOrder == null || t?.customOrder === '') {
          while (Object.values(existingTaskOrders)?.includes(taskCounter)) taskCounter++;
          existingTaskOrders[t?.id] = taskCounter;
          taskOrderMap[t?.id] = parseFloat(`${parentOrder}.${taskCounter}`);
          taskCounter++;
        } else {
          taskOrderMap[t?.id] = Number(t?.customOrder);
        }
      });
    });

    // --- Subtasks ---
    const subtasks = items?.filter(i => i?.rowType === 'subtask');
    const subtaskOrderMap = {};
    const subtasksByParent = {};
    subtasks?.forEach(s => {
      const pid = s?.parentId;
      if (!subtasksByParent?.[pid]) subtasksByParent[pid] = [];
      subtasksByParent?.[pid]?.push(s);
    });
    Object.entries(subtasksByParent)?.forEach(([parentId, parentSubtasks]) => {
      const parentTaskOrder = taskOrderMap?.[parentId] ?? 1.1;
      const parentStr = parentTaskOrder?.toFixed(1);
      const sorted = [...parentSubtasks]?.sort((a, b) => {
        const ad = a?.startDate ? new Date(a?.startDate) : new Date(9999, 0);
        const bd = b?.startDate ? new Date(b?.startDate) : new Date(9999, 0);
        return ad - bd;
      });
      const existingSubOrders = {};
      parentSubtasks?.forEach(s => {
        if (s?.customOrder != null && s?.customOrder !== '') {
          const str = String(Number(s?.customOrder)?.toFixed(2));
          const lastDigit = parseInt(str?.split('.')?.[1]?.slice(-1) || '0');
          existingSubOrders[s?.id] = lastDigit;
        }
      });
      let subCounter = 1;
      sorted?.forEach(s => {
        if (s?.customOrder == null || s?.customOrder === '') {
          while (Object.values(existingSubOrders)?.includes(subCounter)) subCounter++;
          existingSubOrders[s?.id] = subCounter;
          subtaskOrderMap[s?.id] = parseFloat(`${parentStr}${subCounter}`);
          subCounter++;
        } else {
          subtaskOrderMap[s?.id] = Number(s?.customOrder);
        }
      });
    });

    // Build updates only for items that needed order assignment
    const updates = [];
    needsOrder?.forEach(item => {
      let newOrder = null;
      if (item?.rowType === 'deliverable' || (!item?.rowType && !item?.parentId)) {
        newOrder = deliverableOrderMap?.[item?.id];
      } else if (item?.rowType === 'task') {
        newOrder = taskOrderMap?.[item?.id];
      } else if (item?.rowType === 'subtask') {
        newOrder = subtaskOrderMap?.[item?.id];
      }
      if (newOrder != null) {
        updates?.push({ id: item?.id, customOrder: newOrder });
      }
    });

    if (!updates?.length) return { error: null };

    try {
      const promises = updates?.map(({ id, customOrder }) =>
        supabase
          ?.from('deliverables')
          ?.update({ custom_order: customOrder })
          ?.eq('id', id)
      );
      await Promise.all(promises);
      return { updates, error: null };
    } catch (error) {
      console.error('Error initializing orders:', error?.message);
      return { error };
    }
  },

  // Add dependency
  async addDependency(deliverableId, dependsOnId) {
    try {
      const { data, error } = await supabase
        ?.from('deliverable_dependencies')
        ?.insert({
          deliverable_id: deliverableId,
          depends_on_id: dependsOnId
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error adding dependency:', error?.message);
      return { data: null, error };
    }
  },

  // Remove dependency
  async removeDependency(deliverableId, dependsOnId) {
    try {
      const { error } = await supabase
        ?.from('deliverable_dependencies')
        ?.delete()
        ?.eq('deliverable_id', deliverableId)
        ?.eq('depends_on_id', dependsOnId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error removing dependency:', error?.message);
      return { error };
    }
  },

  // Assign resource
  async assignResource(deliverableId, resourceId) {
    try {
      const { data, error } = await supabase
        ?.from('deliverable_resource_assignments')
        ?.insert({
          deliverable_id: deliverableId,
          resource_id: resourceId
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error assigning resource:', error?.message);
      return { data: null, error };
    }
  },

  // Unassign resource
  async unassignResource(deliverableId, resourceId) {
    try {
      const { error } = await supabase
        ?.from('deliverable_resource_assignments')
        ?.delete()
        ?.eq('deliverable_id', deliverableId)
        ?.eq('resource_id', resourceId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error unassigning resource:', error?.message);
      return { error };
    }
  },

  // Check for resource conflicts
  async checkResourceConflicts(resourceId, startDate, endDate, excludeDeliverableId = null) {
    try {
      let query = supabase
        ?.from('deliverable_resource_assignments')
        ?.select(`
          deliverable:deliverables(
            id,
            name,
            start_date,
            end_date,
            project:projects(project_name)
          )
        `)
        ?.eq('resource_id', resourceId);

      const { data, error } = await query;

      if (error) throw error;

      // Filter for date overlaps
      const conflicts = (data || [])?.filter(assignment => {
        const deliverable = assignment?.deliverable;
        if (!deliverable || deliverable?.id === excludeDeliverableId) return false;

        const assignmentStart = new Date(deliverable?.start_date);
        const assignmentEnd = new Date(deliverable?.end_date);
        const checkStart = new Date(startDate);
        const checkEnd = new Date(endDate);

        // Check for overlap
        return assignmentStart <= checkEnd && assignmentEnd >= checkStart;
      })?.map(assignment => toCamelCase(assignment?.deliverable));

      return { data: conflicts, error: null };
    } catch (error) {
      console.error('Error checking resource conflicts:', error?.message);
      return { data: null, error };
    }
  },

  // ── Sort Preference Methods ──────────────────────────────────────────────

  // Save sort method preference for a user+project
  async saveSortPreference(userId, projectId, sortMethod) {
    try {
      const { error } = await supabase
        ?.from('project_sort_preferences')
        ?.upsert(
          {
            user_id: userId,
            project_id: projectId,
            sort_method: sortMethod,
            updated_at: new Date()?.toISOString(),
          },
          { onConflict: 'user_id,project_id' }
        );
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error saving sort preference:', error?.message);
      return { error };
    }
  },

  // Get sort method preference for a user+project
  async getSortPreference(userId, projectId) {
    try {
      const { data, error } = await supabase
        ?.from('project_sort_preferences')
        ?.select('sort_method')
        ?.eq('user_id', userId)
        ?.eq('project_id', projectId)
        ?.single();
      if (error) {
        if (error?.code === 'PGRST116') return { data: 'defined_order', error: null }; // no row = default
        throw error;
      }
      return { data: data?.sort_method || 'defined_order', error: null };
    } catch (error) {
      console.error('Error fetching sort preference:', error?.message);
      return { data: 'defined_order', error };
    }
  },
};